import type { TenantCreatedPayload, TenantInvitation, TenantSummary } from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import { createTenant, listTenants } from '../api.js';

function invitationUrl(invitation: TenantInvitation): string {
	const url = new URL(window.location.href);
	url.search = '';
	url.searchParams.set(
		invitation.kind === 'membership' ? 'membership' : 'setup',
		invitation.kind === 'membership' ? invitation.invitationToken : invitation.setupToken,
	);
	return url.toString();
}

interface PlatformTenantsProps {
	onSelectTenant: (tenantId: string) => void;
}

export function PlatformTenants({ onSelectTenant }: PlatformTenantsProps) {
	const [tenants, setTenants] = useState<TenantSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [showCreate, setShowCreate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [created, setCreated] = useState<TenantCreatedPayload>();

	useEffect(() => {
		void listTenants()
			.then(setTenants)
			.catch((caught: unknown) =>
				setError(caught instanceof Error ? caught.message : 'Could not load customers'),
			)
			.finally(() => setLoading(false));
	}, []);

	async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);

		try {
			const result = await createTenant({
				name: String(data.get('name')),
				ownerDisplayName: String(data.get('ownerDisplayName')),
				ownerEmail: String(data.get('ownerEmail')),
				planCode: String(data.get('planCode')),
			});
			setTenants((current) => [result.tenant, ...current]);
			setCreated(result);
			setShowCreate(false);
			form.reset();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not create customer');
		} finally {
			setBusy(false);
		}
	}

	async function copySetupLink(): Promise<void> {
		if (created) {
			await navigator.clipboard.writeText(invitationUrl(created.ownerSetup));
		}
	}

	return (
		<section className="workspace-section">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform administration</p>
					<h2>Customers</h2>
					<p className="muted">Create and control isolated customer workspaces.</p>
				</div>
				<button
					className="primary-button"
					onClick={() => setShowCreate((visible) => !visible)}
				>
					{showCreate ? 'Cancel' : 'New customer'}
				</button>
			</div>

			{error && <div className="alert error">{error}</div>}
			{created && (
				<div className="setup-result">
					<div>
						<strong>{created.tenant.name} is ready</strong>
						<p>
							{created.ownerSetup.emailDelivery === 'queued'
								? `An invitation email has been queued for ${created.ownerSetup.email}.`
								: `Email delivery is not available, so send this link privately to ${created.ownerSetup.email}.`}{' '}
							{created.ownerSetup.kind === 'membership'
								? 'The owner will sign in with their existing account to accept it. '
								: 'The owner will use it to create their account. '}
							It expires on {new Date(created.ownerSetup.expiresAt).toLocaleString()}
							.{' '}
						</p>
					</div>
					<div className="setup-link-row">
						<input
							readOnly
							value={invitationUrl(created.ownerSetup)}
							aria-label="Invitation link"
						/>
						<button className="secondary-button" onClick={() => void copySetupLink()}>
							Copy link
						</button>
					</div>
				</div>
			)}

			{showCreate && (
				<form className="inline-form tenant-form" onSubmit={(event) => void submit(event)}>
					<div className="form-title">
						<strong>Create customer workspace</strong>
						<span>The owner receives a single-use setup link.</span>
					</div>
					<label>
						<span>Company name (optional)</span>
						<input name="name" maxLength={160} />
					</label>
					<label>
						<span>Owner name</span>
						<input name="ownerDisplayName" required maxLength={160} />
					</label>
					<label>
						<span>Owner email</span>
						<input name="ownerEmail" type="email" required maxLength={320} />
					</label>
					<label>
						<span>Plan</span>
						<select name="planCode" defaultValue="starter">
							<option value="starter">Starter</option>
							<option value="growth">Growth</option>
							<option value="enterprise">Enterprise</option>
						</select>
					</label>
					<button className="primary-button form-submit" disabled={busy}>
						{busy ? 'Creating…' : 'Create customer'}
					</button>
				</form>
			)}

			<div className="table-card">
				<div className="table-head tenant-row standard-table-head">
					<span>Customer</span>
					<span>Plan</span>
					<span>Team</span>
					<span>Status</span>
				</div>
				{loading && <div className="empty-state">Loading customers…</div>}
				{!loading && tenants.length === 0 && (
					<div className="empty-state">No customer workspaces have been created yet.</div>
				)}
				{tenants.map((tenant) => (
					<button
						className="table-row tenant-row tenant-row-link"
						key={tenant.id}
						onClick={() => onSelectTenant(tenant.id)}
						type="button"
					>
						<strong>{tenant.name}</strong>
						<span className="capitalise">{tenant.planCode}</span>
						<span>{tenant.memberCount}</span>
						<span className={`status-pill ${tenant.status}`}>{tenant.status}</span>
					</button>
				))}
			</div>
		</section>
	);
}
